import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { User } from "types/user.types";
import {
  UserInfoGrid,
  InfoCard,
  InfoLabel,
  InfoValue,
  DeviceSerial,
} from "styles/components/userDetails.styles";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";

interface Props {
  user: User;
  isVerifying: boolean;
  onVerify: () => void;
  isTogglingStatus: boolean;
  onToggleStatus: () => void;
  isChangingPrivilege?: boolean;
  /** Called with the newly selected level; the parent confirms and saves. */
  onChangePrivilege?: (privilege: "USER" | "ADMIN") => void;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const UserOverviewTab: React.FC<Props> = ({
  user,
  isVerifying,
  onVerify,
  isTogglingStatus,
  onToggleStatus,
  isChangingPrivilege = false,
  onChangePrivilege,
}) => {
  const navigate = useNavigate();
  const isSuper = user.privledgelevel === "SUPER";
  const privilegeSelectId = `user-${user.id}-privilege`;
  const privilegeHelpId = `${privilegeSelectId}-help`;

  return (
    <div style={{ marginTop: "1rem" }}>
      <UserInfoGrid>
        <InfoCard>
          <InfoLabel>Email</InfoLabel>
          <InfoValue>{user.email}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>City</InfoLabel>
          <InfoValue>{user.city || "N/A"}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Country</InfoLabel>
          <InfoValue>{user.country || "N/A"}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Language</InfoLabel>
          <InfoValue>{user.language || "N/A"}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Linked Device</InfoLabel>
          <InfoValue>
            {user.Device?.serialNumber ? (
              <DeviceSerial
                onClick={() =>
                  navigate(
                    buildDeviceDetailsPath({
                      serialNumber: user.Device!.serialNumber,
                      isAdminView: true,
                    }),
                  )
                }
              >
                {user.Device.serialNumber}
              </DeviceSerial>
            ) : (
              "No device linked"
            )}
          </InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Allows Notifications</InfoLabel>
          <InfoValue>
            {user.allowsnotifications === null
              ? "N/A"
              : user.allowsnotifications
                ? "Yes"
                : "No"}
          </InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Marketing Emails</InfoLabel>
          <InfoValue>
            {user.marketingmaterialok === null
              ? "N/A"
              : user.marketingmaterialok
                ? "Opted in"
                : "Opted out"}
          </InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Product Updates</InfoLabel>
          <InfoValue>
            {user.updatesok === null
              ? "N/A"
              : user.updatesok
                ? "Opted in"
                : "Opted out"}
          </InfoValue>
        </InfoCard>
        <InfoCard>
          {isSuper ? (
            <InfoLabel>Privilege</InfoLabel>
          ) : (
            <InfoLabel as="label" htmlFor={privilegeSelectId}>
              Privilege
            </InfoLabel>
          )}
          <InfoValue>
            {isSuper ? (
              <span id={privilegeSelectId} aria-describedby={privilegeHelpId}>
                SUPER
              </span>
            ) : (
              <select
                id={privilegeSelectId}
                value={user.privledgelevel === "ADMIN" ? "ADMIN" : "USER"}
                disabled={isChangingPrivilege || !onChangePrivilege}
                aria-busy={isChangingPrivilege}
                onChange={(e) =>
                  onChangePrivilege?.(e.target.value as "USER" | "ADMIN")
                }
              >
                <option value="USER">User</option>
                <option value="ADMIN">Admin</option>
              </select>
            )}
            {isSuper && (
              <small id={privilegeHelpId} style={{ display: "block" }}>
                SUPER users cannot be changed from the portal.
              </small>
            )}
          </InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Member Since</InfoLabel>
          <InfoValue>{formatDate(user.createdAt)}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Last Updated</InfoLabel>
          <InfoValue>{formatDate(user.updatedAt)}</InfoValue>
        </InfoCard>
      </UserInfoGrid>

      {!user.isVerified && (
        <div style={{ marginTop: "1.5rem" }}>
          <Button
            label="Verify Account"
            icon="pi pi-check-circle"
            className="p-button-success"
            onClick={onVerify}
            loading={isVerifying}
          />
        </div>
      )}

      <div style={{ marginTop: "1.5rem" }}>
        <Button
          label={user.isdisabled ? "Enable Account" : "Disable Account"}
          icon={user.isdisabled ? "pi pi-user" : "pi pi-ban"}
          className={user.isdisabled ? "p-button-success" : "p-button-danger"}
          onClick={onToggleStatus}
          loading={isTogglingStatus}
        />
      </div>
    </div>
  );
};

export default UserOverviewTab;
